import * as React from 'react';

import {
    DEKSTOP_BREAKPOINT,
    MOBILE_BREAKPOINT,
    TABLET_BREAKPOINT,
    useScreenType,
} from '@gitbook/spine-react';
import { useViewport } from '@gitbook/util-react-hooks';

import type { PropsOf } from '../propsOf';
import { convertStyleToCSS, hashStyle, insertCSSStyle } from '../util';
import {
    normalizeStyle,
    type ImageStyle,
    type StyleProp,
    type TextStyle,
    type ViewStyle,
} from '../web';
import { getSortedBreakpoints } from './getSortedBreakpoints';

export type ResponsiveViewStyle =
    | StyleProp<ViewStyle | TextStyle | ImageStyle>
    | 'hidden'
    | 'visible';

export type ResponsiveViewDefaultStyles = {
    /** Style applied only when the viewport is smaller than the mobile breakpoint */
    mobileStyle?: ResponsiveViewStyle;
    /** Style applied only when the viewport is smaller than the tablet breakpoint */
    tabletStyle?: ResponsiveViewStyle;
    /** Style applied all the time */
    desktopStyle?: ResponsiveViewStyle;
};

export type ResponsiveViewOwnProps = ResponsiveViewDefaultStyles & {
    children?: React.ReactNode;
    style?: StyleProp<ViewStyle | TextStyle | ImageStyle>;
    dataSet?: Record<string, string>;
    testID?: string;

    /**
     * If the mobile or tablet breakpoints aren't enough, you can use this prop
     * to specify styles at custom breakpoints.
     */
    customBreakpointsStyles?: {
        [breakpoint: number]: ResponsiveViewStyle;
    };
};

export type ResponsiveViewProps<E extends React.ElementType = React.ElementType> =
    ResponsiveViewOwnProps & Omit<PropsOf<E>, keyof ResponsiveViewOwnProps>;

/**
 * Allows you to create your own `Responsive` components.
 *
 * The returned component is augmented with 4 new props:
 * - `mobileStyle`: Style applied only when the viewport is smaller than the mobile breakpoint
 * - `tabletStyle`: Style applied only when the viewport is smaller than the tablet breakpoint
 * - `desktopStyle`: Style applied all the time
 * - `customBreakpointsStyles`: If the mobile or tablet breakpoints aren't enough, you can use this prop to specify styles at custom breakpoints.
 *
 * Those 4 props can be a regular style **or** either `"visible"` or `"hidden"'.
 * When the breakpoint matches a prop that is `"hidden"`, the component will not be rendered.
 *
 * -----
 *
 * The difference with the version in `createResponsiveComponent.tsx` is that this one works on SSR:
 * it will always render (regardless of the `hidden` props) and inject some CSS to hide it for the
 * relevant breakpoints. That means that regardless of your device, you will be served the right
 * component. It is a bit slower because we need to unfold all the style props so do you use regular
 * a basic element if you can, but as soon as you use a pattern
 * (for example `<Responsive.View mobileStyle="visible" desktopStyle="hidden" />`), it will be cached
 * so it won't cost more to use it.
 *
 * If you can, avoid changing the new props at runtime.
 */
export function createResponsiveComponent<E extends React.ElementType>(Component: E) {
    const ResponsiveComponent = function ResponsiveComponentWithRef<E extends React.ElementType>({
        ref,
        ...props
    }: ResponsiveViewProps<E> & {
        ref?: React.ComponentPropsWithRef<E>['ref'];
    }): React.ReactElement | null {
        const {
            children,
            style: baseStyle,
            mobileStyle,
            tabletStyle,
            desktopStyle,
            customBreakpointsStyles,
            dataSet,
            testID,
            ...rest
        } = props;

        // we only want to subscribe to the screen dimension if we have custom breakpoints
        const screenDims = useViewport();
        const { isMobile, isTablet } = useScreenType();

        const [data, breakpoints] = React.useMemo(() => {
            const breakpointsMaps = {
                [TABLET_BREAKPOINT]: tabletStyle,
                [MOBILE_BREAKPOINT]: mobileStyle,
                [DEKSTOP_BREAKPOINT]: desktopStyle,
                ...customBreakpointsStyles,
            };

            return [flushBreakpointStyles(breakpointsMaps), breakpointsMaps];
        }, [mobileStyle, tabletStyle, desktopStyle, customBreakpointsStyles]);

        const result = React.createElement(
            Component,
            {
                ...rest,
                ref,
                style: normalizeStyle(baseStyle),
                'data-testid': testID,
                ...Object.fromEntries(
                    Object.entries({ ...dataSet, ...data }).map(([key, value]) => [
                        `data-${key}`,
                        value,
                    ])
                ),
            },
            children
        );

        React.useEffect(() => {
            // once we have rendered once, we set turn this flag off
            // to enable render optimisations.
            window.__GITBOOK_INITIAL_RENDER__ = false;
        }, []);

        // on SSR, we want to styles right away
        if (typeof window === 'undefined') {
            return result;
        }

        const filterFunction = customBreakpointsStyles
            ? (k: string) => Number.parseFloat(k) >= screenDims.width
            : isMobile
              ? (k: string) => Number.parseFloat(k) >= MOBILE_BREAKPOINT
              : isTablet
                ? (k: string) => Number.parseFloat(k) >= TABLET_BREAKPOINT
                : (k: string) => Number.parseFloat(k) >= DEKSTOP_BREAKPOINT;
        const screenStyle = getSortedBreakpoints(breakpoints)
            .filter(filterFunction)
            .reduce((prev, k) => breakpoints[k] || prev, 'visible');

        if (
            screenStyle === 'hidden' &&
            !window.__GITBOOK_INITIAL_RENDER__ &&
            !process.env.GITBOOK_PREDICTABLE_UI
        ) {
            // we are going to avoid rendering the component to improve the performances
            // we *could* render it since it's going to be hidden via CSS but it's better that way
            // That being said, we only do that once the first render after SSR is done to
            // avoid breaking the rehydratation.

            // we only hide if the lowest defined breakpoint is "hidden"
            // (eg. we don't bubble the hidden property)
            return null;
        }

        return result;
    };

    if (typeof Component !== 'string') {
        ResponsiveComponent.displayName = `Responsive(${Component.displayName || Component.name})`;
    }

    return ResponsiveComponent;
}

/** Encode a string as a really big number string */
function getClassNameEncoded(className: string) {
    let result = '';
    for (let i = 0; i < className.length; i++) {
        result += `${className.charCodeAt(i)}`;
    }
    return result;
}

function flushBreakpointStyles(
    breakpoints: Record<number, ResponsiveViewStyle>
): Record<string, boolean> {
    const data: Record<string, boolean> = {};

    if (breakpoints[DEKSTOP_BREAKPOINT]) {
        flushBreakpointStyle(data, DEKSTOP_BREAKPOINT, breakpoints[DEKSTOP_BREAKPOINT], 10);
    }

    getSortedBreakpoints(breakpoints)
        .filter((k) => Number.parseFloat(k) !== DEKSTOP_BREAKPOINT) // filter out the desktop breakpoint
        .forEach((breakpoint, index) => {
            const breakpointPx = Number.parseFloat(breakpoint);
            if (breakpoints[breakpoint]) {
                flushBreakpointStyle(data, breakpointPx, breakpoints[breakpoint], 10 + index + 1);
            }
        });

    return data;
}

function flushBreakpointStyle(
    data: Record<string, boolean>,
    breakpoint: number,
    style: ResponsiveViewStyle,
    cssGroup: number
): void {
    const hash = hashStyle(style);

    if (!hash) {
        // No style
        return;
    }

    const [id, newId] = getId(
        `${breakpoint === Number.MAX_VALUE ? 'desktop' : breakpoint}-${hash}`
    );
    data[getDataKey(id)] = true;

    if (!newId) {
        // We already flushed this style
        return;
    }

    const css = convertStyleToCSS(style);

    if (breakpoint === DEKSTOP_BREAKPOINT) {
        insertCSSStyle(`[${getDataAttribute(id)}]{${css}}`, cssGroup);
    } else {
        // this is a bit of a hack (understatement of the year)
        // we add a media that will always be true (max-width: near infinite)
        // in order to have a unique selector for RNW
        // (it takes the part before the first `{`)
        // because it only really insert in the sheet when it's different
        // The extra media query keeps each generated rule unique in the stylesheet.
        // Unfortunately, Firefox rounds the width number when we inject it and it causes an issue for mobile styles.
        const mediaSelectorHack = `(max-width: 9999999.${getClassNameEncoded(id)}px)`;

        insertCSSStyle(
            `@media (max-width: ${breakpoint}px) and ${mediaSelectorHack} { [${getDataAttribute(
                id
            )}] {${css}} }`,

            // We insert the styles to avoid conflicts between the desktop one and the other breakpoints
            // the order should always have desktop first (apply to all) and then the other breakpoints
            cssGroup
        );
    }
}

const ids = new Set<string>();

function getId(hash: string): [string, boolean] {
    const id = hash.toLowerCase();

    if (typeof window === 'undefined') {
        return [id, true];
    }

    if (ids.has(id)) {
        return [id, false];
    }

    ids.add(id);
    return [id, true];
}

function getDataKey(id: string): string {
    return `rnwr${id}`;
}

function getDataAttribute(id: string): string {
    return `data-${getDataKey(id)}`;
}
